import React, { useState } from "react";

/** Variables an admin can drop into the subject or intro. */
const AVAILABLE_VARIABLES = [
  "dataOwnerName",
  "auditorName",
  "reportPeriod",
  "dueDate",
  "questionsPending",
  "totalPending",
];

/**
 * Editor for one template's slots.
 *
 * Seeded from the resolved values, so the fields show what recipients currently
 * receive rather than empty boxes. The parent remounts this on template change,
 * which is why the initial state may be taken from props here.
 * @param {object} props
 * @param {object} props.slots the resolved slot values.
 * @param {(form: object) => Promise<{ok: boolean, message?: string}>} props.onSubmit saves the slots.
 * @param {() => Promise<{ok: boolean, message?: string}>} props.onReset reverts to defaults.
 * @returns {JSX.Element}
 */
export default function SlotForm({ slots, onSubmit, onReset }) {
  const [form, setForm] = useState({
    subject: slots.subject || "",
    introText: slots.intro || "",
    signOff: slots.signOff || "",
    logoUrl: slots.logoUrl || "",
    accentColour: slots.accentColour || "#2d387b",
  });
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  /**
   * Runs a mutation and reports the outcome inline.
   * @param {() => Promise<{ok: boolean, message?: string}>} action the mutation.
   * @param {string} successText message shown when it succeeds.
   */
  const run = async (action, successText) => {
    setBusy(true);
    const res = await action();
    setBusy(false);
    setFeedback(
      res.ok
        ? { ok: true, text: successText }
        : { ok: false, text: res.message || "Could not save" },
    );
  };

  return (
    <div className="emt-form">
      <label className="emt-label" htmlFor="emt-subject">
        Subject
      </label>
      <input
        id="emt-subject"
        className="emt-input"
        value={form.subject}
        placeholder="Reminder: {{reportPeriod}} submissions due"
        onChange={(e) => set({ subject: e.target.value })}
      />

      <label className="emt-label" htmlFor="emt-intro">
        Opening paragraph
      </label>
      <textarea
        id="emt-intro"
        className="emt-textarea"
        rows={3}
        value={form.introText}
        onChange={(e) => set({ introText: e.target.value })}
      />

      <p className="emt-hint">
        Available variables:{" "}
        {AVAILABLE_VARIABLES.map((v) => `{{${v}}}`).join(", ")}
      </p>

      <label className="emt-label" htmlFor="emt-signoff">
        Sign-off
      </label>
      <input
        id="emt-signoff"
        className="emt-input"
        value={form.signOff}
        onChange={(e) => set({ signOff: e.target.value })}
      />

      <label className="emt-label" htmlFor="emt-logo">
        Logo URL
      </label>
      <input
        id="emt-logo"
        className="emt-input"
        type="url"
        value={form.logoUrl}
        onChange={(e) => set({ logoUrl: e.target.value })}
      />

      <label className="emt-label" htmlFor="emt-accent">
        Accent colour
      </label>
      <div className="emt-colour-row">
        <input
          id="emt-accent"
          className="emt-colour"
          type="color"
          value={form.accentColour}
          onChange={(e) => set({ accentColour: e.target.value })}
        />
        <span className="emt-muted">{form.accentColour}</span>
      </div>

      <div className="emt-actions">
        <button
          type="button"
          className="emt-btn"
          disabled={busy}
          onClick={() => run(() => onSubmit(form), "Template saved.")}
        >
          {busy ? "Saving…" : "Save changes"}
        </button>
        <button
          type="button"
          className="emt-btn emt-btn-ghost"
          disabled={busy}
          onClick={() => run(onReset, "Reverted to the default template.")}
        >
          Reset to default
        </button>
      </div>

      {feedback && (
        <p className={feedback.ok ? "emt-muted" : "emt-error"} role="status">
          {feedback.text}
        </p>
      )}
    </div>
  );
}
